import React, { useEffect, useState, createContext, useContext } from 'react';
import { Session, Meeting } from '../types';
interface MyScheduleContextType {
  bookedSessions: Session[];
  meetings: Meeting[];
  addBookedSession: (session: Session) => void;
  removeBookedSession: (sessionId: string) => void;
  addMeeting: (meeting: Meeting) => void;
  updateMeeting: (meetingId: string, updates: Partial<Meeting>) => void;
  removeMeeting: (meetingId: string) => void;
  getConfirmedMeetings: () => Meeting[];
  getAllScheduleItems: () => Array<Session | Meeting>;
}
const MyScheduleContext = createContext<MyScheduleContextType | undefined>(undefined);
export function MyScheduleProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Initialize with realistic default meetings using real attendee data
  const [meetings, setMeetings] = useState<Meeting[]>([{
    id: 'default-1',
    attendeeId: 'attendee-50',
    attendeeName: 'Amy Khan',
    attendeeCompany: 'Fitness First',
    attendeeRole: 'VP of Sales',
    date: 'October 25, 2025',
    startTime: '14:00',
    endTime: '14:30',
    location: 'Fitness First Stand',
    status: 'confirmed',
    requestedBy: 'me'
  }, {
    id: 'default-2',
    attendeeId: 'attendee-47',
    attendeeName: 'Dylan Al-Mansoori',
    attendeeCompany: 'Optimum Nutrition',
    attendeeRole: 'Director of Marketing',
    date: 'October 26, 2025',
    startTime: '10:00',
    endTime: '10:30',
    location: 'Stand A12B',
    status: 'pending',
    requestedBy: 'me'
  }, {
    id: 'default-3',
    attendeeId: 'attendee-29',
    attendeeName: 'Benjamin Allen',
    attendeeCompany: 'Nike',
    attendeeRole: 'Director of Marketing',
    date: 'October 24, 2025',
    startTime: '15:00',
    endTime: '15:30',
    location: 'Stand C7c',
    status: 'pending',
    requestedBy: 'them'
  }, {
    id: 'invite-1',
    attendeeId: 'attendee-2',
    attendeeName: 'Emma Williams',
    attendeeCompany: 'Enhance Fitness',
    attendeeRole: 'Owner',
    date: 'October 25, 2025',
    startTime: '11:00',
    endTime: '11:30',
    location: 'Stand A2A',
    status: 'pending',
    requestedBy: 'them'
  }, {
    id: 'invite-2',
    attendeeId: 'attendee-5',
    attendeeName: 'David Garcia',
    attendeeCompany: 'Technogym',
    attendeeRole: 'Owner',
    date: 'October 26, 2025',
    startTime: '14:00',
    endTime: '14:30',
    location: 'Stand B3',
    status: 'pending',
    requestedBy: 'them'
  }]);
  const [bookedSessions, setBookedSessions] = useState<Session[]>([]);
  // Persist to localStorage
  useEffect(() => {
    const savedMeetings = localStorage.getItem('myScheduleMeetings');
    const savedSessions = localStorage.getItem('myScheduleSessions');
    if (savedMeetings) {
      try {
        const parsed = JSON.parse(savedMeetings);
        setMeetings(parsed);
      } catch (e) {
        console.error('Failed to parse saved meetings', e);
      }
    }
    if (savedSessions) {
      try {
        const parsed = JSON.parse(savedSessions);
        setBookedSessions(parsed);
      } catch (e) {
        console.error('Failed to parse saved sessions', e);
      }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem('myScheduleMeetings', JSON.stringify(meetings));
  }, [meetings]);
  useEffect(() => {
    localStorage.setItem('myScheduleSessions', JSON.stringify(bookedSessions));
  }, [bookedSessions]);
  const addBookedSession = (session: Session) => {
    setBookedSessions(prev => {
      // Avoid duplicates
      if (prev.find(s => s.id === session.id)) return prev;
      return [...prev, {
        ...session,
        isBooked: true
      }];
    });
  };
  const removeBookedSession = (sessionId: string) => {
    setBookedSessions(prev => prev.filter(s => s.id !== sessionId));
  };
  const addMeeting = (meeting: Meeting) => {
    setMeetings(prev => {
      // Avoid duplicates
      if (prev.find(m => m.id === meeting.id)) return prev;
      return [...prev, meeting];
    });
  };
  const updateMeeting = (meetingId: string, updates: Partial<Meeting>) => {
    setMeetings(prev => prev.map(m => m.id === meetingId ? {
      ...m,
      ...updates
    } : m));
  };
  const removeMeeting = (meetingId: string) => {
    setMeetings(prev => prev.filter(m => m.id !== meetingId));
  };
  const getConfirmedMeetings = () => {
    return meetings.filter(m => m.status === 'confirmed');
  };
  const getAllScheduleItems = () => {
    return [...bookedSessions, ...meetings];
  };
  return <MyScheduleContext.Provider value={{
    bookedSessions,
    meetings,
    addBookedSession,
    removeBookedSession,
    addMeeting,
    updateMeeting,
    removeMeeting,
    getConfirmedMeetings,
    getAllScheduleItems
  }} data-id="element-4269">
      {children}
    </MyScheduleContext.Provider>;
}
export function useMySchedule() {
  const context = useContext(MyScheduleContext);
  if (context === undefined) {
    throw new Error('useMySchedule must be used within a MyScheduleProvider');
  }
  return context;
}